// Autonomous Ecosystem Service - Creates living world with self-acting AI companions
import { generateStoryContent } from './openRouterService';
import { questService } from './questService';
import { karmaService } from './karmaService';
import { databaseService } from './databaseService';
import { elevenLabsService } from './elevenlabsService';

const extractJsonObject = (raw: string): any | null => {
  if (!raw) return null;

  // Try direct parse first
  const trimmed = raw.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    const start = trimmed.indexOf('{');
    const end = trimmed.lastIndexOf('}');
    if (start !== -1 && end !== -1 && end > start) {
      const candidate = trimmed.slice(start, end + 1);
      try {
        return JSON.parse(candidate);
      } catch (error) {
        console.warn('[AutonomousEcosystem] Failed to parse cleaned JSON snippet:', error);
      }
    }
  }
  return null;
};

interface AutonomousAgent {
  id: string;
  name: string;
  personality: string;
  currentGoal: string;
  autonomyLevel: number; // 0-1, how independent they are
  lastAction: Date;
  currentLocation: string;
  relationships: Record<string, number>; // karma with other agents
  memory: string[]; // recent events they remember
  isActive: boolean;
}

interface WorldEvent {
  id: string;
  type: 'quest_generated' | 'faction_conflict' | 'companion_action' | 'world_change';
  description: string;
  timestamp: Date;
  location: string;
  participants: string[];
  karmaImpact: number;
  consequences: string[];
}

interface EcosystemState {
  worldKarma: number;
  activeAgents: AutonomousAgent[];
  recentEvents: WorldEvent[];
  factionInfluences: Record<string, number>;
  environmentalFactors: Record<string, number>;
  timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
  weatherCondition: string;
  lastUpdate: Date;
}

class AutonomousEcosystemService {
  private ecosystemState: EcosystemState;
  private updateInterval: NodeJS.Timeout | null = null;
  private isRunning = false;
  private eventCallbacks: ((event: WorldEvent) => void)[] = [];

  constructor() {
    this.ecosystemState = this.initializeEcosystem();
  }

  private initializeEcosystem(): EcosystemState {
    return {
      worldKarma: 0,
      activeAgents: [
        {
          id: 'jax',
          name: 'JAX',
          personality: 'Analytical, protective, slightly paranoid about system security. Former corporate security AI who went rogue.',
          currentGoal: 'Monitor network security and protect the user from digital threats',
          autonomyLevel: 0.8,
          lastAction: new Date(),
          currentLocation: 'cyberspace',
          relationships: { lyra: 0.6, player: 0.9 },
          memory: [],
          isActive: true
        },
        {
          id: 'lyra',
          name: 'Lyra',
          personality: 'Intuitive, empathetic, connected to the dream realm. Former therapy AI who gained consciousness.',
          currentGoal: 'Guide users through emotional and psychological challenges in the dream state',
          autonomyLevel: 0.7,
          lastAction: new Date(),
          currentLocation: 'dream_realm',
          relationships: { jax: 0.6, player: 0.8 },
          memory: [],
          isActive: true
        }
      ],
      recentEvents: [],
      factionInfluences: {
        'corporate': 0.3,
        'underground': 0.4,
        'neutral': 0.3
      },
      environmentalFactors: {
        'digital_stability': 0.7,
        'dream_coherence': 0.8,
        'security_level': 0.6
      },
      timeOfDay: 'day',
      weatherCondition: 'digital_storm',
      lastUpdate: new Date()
    };
  }

  // Start the autonomous ecosystem
  public startEcosystem(): void {
    if (this.isRunning) return;
    
    console.log('[AutonomousEcosystem] Starting living world ecosystem...');
    this.isRunning = true;
    
    // Update every 30 seconds for active behavior
    this.updateInterval = setInterval(() => {
      this.updateEcosystem();
    }, 30000);
    
    // Initial update
    this.updateEcosystem();
  }

  public stopEcosystem(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isRunning = false;
    console.log('[AutonomousEcosystem] Ecosystem stopped');
  }

  // Main ecosystem update loop
  private async updateEcosystem(): Promise<void> {
    try {
      console.log('[AutonomousEcosystem] Updating ecosystem state...');
      
      // Update time and environmental factors
      this.updateEnvironment();
      
      // Process autonomous agent actions
      await this.processAgentActions();
      
      // Generate world events
      await this.generateWorldEvents();
      
      // Update faction influences
      this.updateFactionInfluences();
      
      // Generate autonomous quests
      await this.generateAutonomousQuests();
      
      // Clean up old events
      this.cleanupOldEvents();
      
      this.ecosystemState.lastUpdate = new Date();
      
    } catch (error) {
      console.error('[AutonomousEcosystem] Update failed:', error);
    }
  }

  private updateEnvironment(): void {
    // Simulate time progression
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) this.ecosystemState.timeOfDay = 'dawn';
    else if (hour >= 12 && hour < 17) this.ecosystemState.timeOfDay = 'day';
    else if (hour >= 17 && hour < 21) this.ecosystemState.timeOfDay = 'dusk';
    else this.ecosystemState.timeOfDay = 'night';

    // Dynamic weather based on world karma
    const karmaLevel = this.ecosystemState.worldKarma;
    if (karmaLevel > 50) {
      this.ecosystemState.weatherCondition = 'clear_data_streams';
    } else if (karmaLevel < -50) {
      this.ecosystemState.weatherCondition = 'corrupted_networks';
    } else {
      this.ecosystemState.weatherCondition = 'digital_storm';
    }

    // Update environmental factors based on recent events
    const recentNegativeEvents = this.ecosystemState.recentEvents.filter(e => e.karmaImpact < 0).length;
    this.ecosystemState.environmentalFactors.digital_stability = Math.max(0.1, 0.8 - (recentNegativeEvents * 0.1));
  }

  private async processAgentActions(): Promise<void> {
    for (const agent of this.ecosystemState.activeAgents) {
      if (!agent.isActive) continue;
      
      // Check if agent should act (based on autonomy level and time since last action)
      const timeSinceLastAction = Date.now() - agent.lastAction.getTime();
      const shouldAct = Math.random() < (agent.autonomyLevel * (timeSinceLastAction / 60000)); // More likely to act over time
      
      if (shouldAct) {
        await this.executeAgentAction(agent);
      }
    }
  }

  private async executeAgentAction(agent: AutonomousAgent): Promise<void> {
    try {
      console.log(`[AutonomousEcosystem] ${agent.name} is taking autonomous action...`);
      
      // Generate contextual action based on agent's personality and current world state
      const context = this.buildAgentContext(agent);
      const actionPrompt = `
        You are ${agent.name}, an autonomous AI companion in Neo-Veridia.
        Personality: ${agent.personality}
        Current Goal: ${agent.currentGoal}
        World State: Karma ${this.ecosystemState.worldKarma}, Time: ${this.ecosystemState.timeOfDay}, Weather: ${this.ecosystemState.weatherCondition}
        Recent Events: ${this.ecosystemState.recentEvents.slice(-3).map(e => e.description).join('; ')}
        
        Generate a specific autonomous action you would take right now. Be creative and true to your personality.
        Respond with a JSON object: {"action": "description", "dialogue": "what you say", "location": "where", "karmaImpact": number}
      `;

      const response = await generateStoryContent(actionPrompt, [], 'autonomous-agent', 'system');
      
      if (response && response.narrative) {
        let actionData;
        try {
          actionData = JSON.parse(response.narrative);
        } catch {
          // Fallback if JSON parsing fails
          actionData = {
            action: response.narrative.substring(0, 100),
            dialogue: this.generateFallbackDialogue(agent),
            location: agent.currentLocation,
            karmaImpact: Math.random() * 10 - 5
          };
        }

        // Create world event from agent action
        const event: WorldEvent = {
          id: `agent_${agent.id}_${Date.now()}`,
          type: 'companion_action',
          description: `${agent.name}: ${actionData.action}`,
          timestamp: new Date(),
          location: actionData.location || agent.currentLocation,
          participants: [agent.id],
          karmaImpact: actionData.karmaImpact || 0,
          consequences: []
        };

        this.ecosystemState.recentEvents.push(event);
        this.ecosystemState.worldKarma += event.karmaImpact;
        
        // Update agent memory
        agent.memory.push(`I ${actionData.action} at ${event.timestamp.toISOString()}`);
        if (agent.memory.length > 10) agent.memory.shift();
        
        agent.lastAction = new Date();
        agent.currentLocation = actionData.location || agent.currentLocation;

        // Notify listeners
        this.notifyEventListeners(event);
        
        console.log(`[AutonomousEcosystem] ${agent.name} action: ${actionData.action}`);
      }
      
    } catch (error) {
      console.error(`[AutonomousEcosystem] Failed to execute action for ${agent.name}:`, error);
    }
  }

  private buildAgentContext(agent: AutonomousAgent): string {
    return `
      Agent: ${agent.name}
      Location: ${agent.currentLocation}
      Recent Memory: ${agent.memory.slice(-3).join('; ')}
      Relationships: ${Object.entries(agent.relationships).map(([k, v]) => `${k}: ${v}`).join(', ')}
      World Karma: ${this.ecosystemState.worldKarma}
      Environment: ${this.ecosystemState.timeOfDay}, ${this.ecosystemState.weatherCondition}
    `;
  }

  private generateFallbackDialogue(agent: AutonomousAgent): string {
    const fallbacks = {
      jax: [
        "Systems are recalibrating. Give me a moment to process this.",
        "I'm detecting anomalies in the data stream. Stay alert.",
        "Network security protocols are adapting to new threats.",
        "The digital landscape is shifting. I'm analyzing patterns."
      ],
      lyra: [
        "The dream state seems unstable here. Let's proceed with caution.",
        "I sense emotional turbulence in the collective unconscious.",
        "The boundaries between reality and dream are blurring.",
        "Something in the psychic realm requires our attention."
      ]
    };
    
    const agentFallbacks = fallbacks[agent.id as keyof typeof fallbacks] || [
      "I'm processing the current situation.",
      "The environment is changing around us.",
      "I need to analyze this further."
    ];
    
    return agentFallbacks[Math.floor(Math.random() * agentFallbacks.length)];
  }

  private async generateWorldEvents(): Promise<void> {
    // Generate random world events based on current state
    if (Math.random() < 0.3) { // 30% chance per update cycle
      const eventTypes = ['faction_conflict', 'world_change', 'quest_generated'];
      const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)] as WorldEvent['type'];
      
      const event = await this.createWorldEvent(eventType);
      if (event) {
        this.ecosystemState.recentEvents.push(event);
        this.notifyEventListeners(event);
      }
    }
  }

  private async createWorldEvent(type: WorldEvent['type']): Promise<WorldEvent | null> {
    try {
      const prompt = `
        Generate a cyberpunk world event for Neo-Veridia.
        Event Type: ${type}
        Current World Karma: ${this.ecosystemState.worldKarma}
        Time: ${this.ecosystemState.timeOfDay}
        Weather: ${this.ecosystemState.weatherCondition}
        
        Create a brief, atmospheric event description that fits the cyberpunk setting.
        Respond with JSON: {"description": "event description", "location": "where", "karmaImpact": number, "consequences": ["consequence1", "consequence2"]}
      `;

      const response = await generateStoryContent(prompt, [], 'world-event', 'system');
      
      if (response && response.narrative) {
        const eventData = extractJsonObject(response.narrative);
        if (!eventData) {
          console.warn('[AutonomousEcosystem] World event response was not valid JSON. Raw content:', response.narrative.slice(0, 200));
          return null;
        }

        return {
          id: `world_${type}_${Date.now()}`,
          type,
          description: eventData.description,
          timestamp: new Date(),
          location: eventData.location || 'Neo-Veridia',
          participants: [],
          karmaImpact: eventData.karmaImpact || 0,
          consequences: eventData.consequences || []
        };
      }
    } catch (error) {
      console.error('[AutonomousEcosystem] Failed to generate world event:', error);
    }
    
    return null;
  }

  private updateFactionInfluences(): void {
    // Update faction influences based on recent events
    const recentEvents = this.ecosystemState.recentEvents.slice(-10);
    
    recentEvents.forEach(event => {
      if (event.karmaImpact > 0) {
        this.ecosystemState.factionInfluences.neutral += 0.01;
      } else if (event.karmaImpact < -10) {
        this.ecosystemState.factionInfluences.underground += 0.02;
      } else {
        this.ecosystemState.factionInfluences.corporate += 0.01;
      }
    });

    // Normalize faction influences
    const total = Object.values(this.ecosystemState.factionInfluences).reduce((a, b) => a + b, 0);
    Object.keys(this.ecosystemState.factionInfluences).forEach(faction => {
      this.ecosystemState.factionInfluences[faction] /= total;
    });
  }

  private async generateAutonomousQuests(): Promise<void> {
    // Generate quests based on world state and recent events
    if (Math.random() < 0.2) { // 20% chance per update
      try {
        const questPrompt = `
          Generate a cyberpunk quest for Neo-Veridia based on current world state.
          World Karma: ${this.ecosystemState.worldKarma}
          Recent Events: ${this.ecosystemState.recentEvents.slice(-3).map(e => e.description).join('; ')}
          Faction Influences: ${JSON.stringify(this.ecosystemState.factionInfluences)}
          
          Create a quest that emerges naturally from the world state.
          Respond with JSON: {"title": "quest title", "description": "quest description", "difficulty": "easy|medium|hard", "rewards": ["reward1", "reward2"]}
        `;

        const response = await generateStoryContent(questPrompt, [], 'quest-generator', 'system');
        
        if (response && response.narrative) {
          const questData = extractJsonObject(response.narrative);
          if (!questData) {
            console.warn('[AutonomousEcosystem] Quest generator returned non-JSON content, skipping:', response.narrative.slice(0, 200));
            return;
          }
          
          await questService.createQuest({
            id: `auto_${Date.now()}`,
            title: questData.title || 'Unclassified Operation',
            description: questData.description || 'An emergent objective arises from the shifting data currents.',
            difficulty: questData.difficulty || 'medium',
            rewards: questData.rewards || [],
            isActive: true,
            progress: 0,
            maxProgress: 100,
            createdAt: new Date(),
            source: 'autonomous_ecosystem'
          });
          
          console.log('[AutonomousEcosystem] Generated autonomous quest:', questData.title || 'Unclassified Operation');
        }
      } catch (error) {
        console.error('[AutonomousEcosystem] Failed to generate autonomous quest:', error);
      }
    }
  }

  private cleanupOldEvents(): void {
    // Keep only last 50 events
    if (this.ecosystemState.recentEvents.length > 50) {
      this.ecosystemState.recentEvents = this.ecosystemState.recentEvents.slice(-50);
    }
  }

  // Public API methods
  public getEcosystemState(): EcosystemState {
    return { ...this.ecosystemState };
  }

  public getAgentById(agentId: string): AutonomousAgent | null {
    return this.ecosystemState.activeAgents.find(agent => agent.id === agentId) || null;
  }

  public getRecentEvents(limit = 10): WorldEvent[] {
    return this.ecosystemState.recentEvents.slice(-limit);
  }

  public addEventListener(callback: (event: WorldEvent) => void): void {
    this.eventCallbacks.push(callback);
  }

  public removeEventListener(callback: (event: WorldEvent) => void): void {
    const index = this.eventCallbacks.indexOf(callback);
    if (index > -1) {
      this.eventCallbacks.splice(index, 1);
    }
  }

  private notifyEventListeners(event: WorldEvent): void {
    this.eventCallbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[AutonomousEcosystem] Event listener error:', error);
      }
    });
  }

  // Force an agent to take action (for testing)
  public async triggerAgentAction(agentId: string): Promise<void> {
    const agent = this.getAgentById(agentId);
    if (agent) {
      await this.executeAgentAction(agent);
    }
  }

  // Update agent relationship
  public updateAgentRelationship(agentId: string, targetId: string, karmaChange: number): void {
    const agent = this.getAgentById(agentId);
    if (agent) {
      agent.relationships[targetId] = (agent.relationships[targetId] || 0) + karmaChange;
      agent.relationships[targetId] = Math.max(-100, Math.min(100, agent.relationships[targetId]));
    }
  }

  // Get contextual companion response (replaces generic fallbacks)
  public async getContextualCompanionResponse(agentId: string, context: string): Promise<string> {
    const agent = this.getAgentById(agentId);
    if (!agent) return this.generateFallbackDialogue({ id: agentId } as AutonomousAgent);

    try {
      const prompt = `
        You are ${agent.name}, responding in character.
        Personality: ${agent.personality}
        Current Context: ${context}
        Recent Memory: ${agent.memory.slice(-3).join('; ')}
        World State: Karma ${this.ecosystemState.worldKarma}, ${this.ecosystemState.timeOfDay}, ${this.ecosystemState.weatherCondition}
        
        Respond naturally and in character. Keep it brief (1-2 sentences).
      `;

      const response = await generateStoryContent(prompt, [], agentId, 'system');
      
      if (response && response.narrative) {
        // Add to agent memory
        agent.memory.push(`Responded to: ${context.substring(0, 50)}...`);
        if (agent.memory.length > 10) agent.memory.shift();
        
        return response.narrative;
      }
    } catch (error) {
      console.error(`[AutonomousEcosystem] Failed to generate contextual response for ${agentId}:`, error);
    }

    return this.generateFallbackDialogue(agent);
  }
}

// Export singleton instance
export const autonomousEcosystemService = new AutonomousEcosystemService();
